import { useEffect, useReducer } from "react";
import { extractApiMessage, readJsonSafe } from "@/lib/adminDisplay";

const initial = { data: null, loading: true, error: null };

function reducer(state, action) {
  switch (action.type) {
    case "start":
      return { ...state, loading: true };
    case "ok":
      return { data: action.data, loading: false, error: null };
    case "err":
      return { data: state.data, loading: false, error: action.error };
    default:
      return state;
  }
}

/**
 * Tiny authenticated-fetch hook that runs `apiFetch(path)` whenever any of
 * `deps` change. Returns `{ data, loading, error, reload }`.
 *
 * Encapsulating the fetch + state machine inside a custom hook keeps the
 * setState calls out of the consumer component's `useEffect` body, which
 * satisfies React 19's stricter `react-hooks/set-state-in-effect` rule.
 */
export function useAdminFetch(apiFetch, path, deps = []) {
  const [state, dispatch] = useReducer(reducer, initial);

  // We deliberately depend on `path` + caller-provided deps; `apiFetch` is a
  // closure that flips identity per auth state.
  useEffect(() => {
    let cancelled = false;
    dispatch({ type: "start" });
    apiFetch(path).then(async (res) => {
      if (cancelled) return;
      const data = await readJsonSafe(res);
      if (!res.ok) {
        dispatch({ type: "err", error: extractApiMessage(res, data, `HTTP ${res.status}`) });
        return;
      }
      if (!cancelled) dispatch({ type: "ok", data });
    }).catch((err) => {
      if (!cancelled) dispatch({ type: "err", error: err?.message || "fetch failed" });
    });
    return () => { cancelled = true; };
  }, [apiFetch, path, ...deps]);

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    reload: () => dispatch({ type: "start" }), // consumer can use a tick-deps to fully refresh
  };
}
